import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import Commongrid from "../../common/Commongrid";
import toast from "react-hot-toast";
import Loading from "../../common/Laoding";
import { capsconversion } from "../../common/capsconversion";


const KnowYourAgent = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const fleetBookingStatus = JSON.parse(
    localStorage.getItem("catalogValues"),
  )?.fleetBookingStatus;
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedAgent, setSelectedAgent] = useState("");
  const [agentsList, setAgentsList] = useState([]);
  const [activeTab, setActiveTab] = useState("todayBookings");
  const [agentTab, setAgentTab] = useState("todayBookings");
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 40,
  });
  const [expandedAccordions, setExpandedAccordions] = useState({
  bookingsDetails: true,
  teammates: true,
  source: true
});
const toggleAccordion = (section) => {
  setExpandedAccordions(prev => ({
    ...prev,
    [section]: !prev[section]
  }));
};

  // GET AGENTS API
  const getAgents = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${apiUrl}/api/users?limit=100&isSalesAgent=true`,
        { headers: { Authorization: token } },
      );
      const data = await res.json();
      if (data?.statusCode === "0100") {
        setAgentsList(data.entities.rows || []);
      }
    } catch {
      toast.error("Failed to load agents");
    } finally {
    setLoading(false); 
  }
  };

  // GET DASHBOARD DATA API
  const getDashboardData = async () => {
    try {
      setIsLoading(true);
      setLoading(true);
      const res = await fetch(
        `${apiUrl}/api/salesagentdashboard/know-your-sales-agent?`,
        { headers: { Authorization: token } },
      );

      const data = await res.json();
      if (data?.statusCode === "0100") {
        setDashboardData(data.dataValues);
      }
    } catch {
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      getAgents();
      getDashboardData();
    }
  }, [token]);
  

  // HELPERS
  const getAgentName = (id) => {
    const agent = agentsList.find((a) => a?.id === Number(id));
    return agent ? agent.name : id;
  };
  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  //  DATA SWITCH
  const getFinalTableData = () => {
    if (!dashboardData) return [];

    //  If source is selected
    if (selectedSource) {
      if (agentTab === "todayBookings") {
        return (dashboardData?.todayMobileAndWebBookingList || [])
          .filter(item => item.source === selectedSource);
      }

      if (agentTab === "monthlyBookings") {
        return (dashboardData?.currentMonthMobileAndWebBookingList || [])
          .filter(item => item.source === selectedSource);
      }
    }

    //  If agent is selected
    if (selectedAgent) {
      const agentId = [String(selectedAgent)];

      if (agentTab === "todayBookings") {
        return dashboardData?.todaysGroupByAgentWiseBookings?.[agentId] || [];
      }

      if (agentTab === "monthlyBookings") {
        return dashboardData?.overallGroupByAgentWiseBookings?.[agentId] || [];
      }

      return [];
    }

    //  If NO agent selected
    if (activeTab === "unassignedInquiry") {
      return dashboardData?.inquiryUnAssignedList || [];
    }

    if (activeTab === "assignedInquiry") {
      return dashboardData?.inquiryList || [];
    }

    if (activeTab === "todayBookings") {
      const obj = dashboardData?.todaysGroupByAgentWiseBookings || {};
      return Object.values(obj).flat();
    }

    if (activeTab === "overallBookings") {
      const obj = dashboardData?.overallGroupByAgentWiseBookings || {};
      return Object.values(obj).flat();
    }

    return [];
  };

  // ROWS
  const tableData = getFinalTableData();

  const paginatedData = React.useMemo(() => {
    const start = paginationModel?.page * paginationModel?.pageSize;
    return tableData.slice(start, start + paginationModel?.pageSize);
  }, [tableData, paginationModel]);

  const mapTableRow = (item, index) => ({
    id: item?.id ?? index,
    ...item || "-",
    assignedAgent: item?.assignedAgent ? getAgentName(item?.assignedAgent) : "",
    bookingStatus: item?.bookingStatus
      ? fleetBookingStatus?.[item?.bookingStatus]
      : "",
  });

  const rows = paginatedData.map(mapTableRow);

  const isAgent = !!selectedAgent;
  const isSource = !!selectedSource;

  // COLUMNS
  const baseColumns = [
    {
      field: "inquiryCode",
      headerName: "Inquiry Code",
      minWidth: 180,
    },
    {
      field: "bookingCode",
      headerName: "Booking Id",
      minWidth: 180,
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      minWidth: 160,
    },
    {
      field: "customerMobile",
      headerName: "Customer Mobile",
      minWidth: 150,
    },
    {
      field: "customerEmail",
      headerName: "CustomerEmail",
      minWidth: 200,
    },
    {
      field: "bookFrom",
      headerName: "Book From",
      minWidth: 180,
    },
    {
      field: "bookTo",
      headerName: "Book To",
      minWidth: 180,
    },
    {
      field: "costToCustomer",
      headerName: "Cost To Customer",
      minWidth: 140,
    },
    {
      field: "weekdayRent",
      headerName: "Weekday Rent",
      minWidth: 150,
    },
    {
      field: "weekendRent",
      headerName: "Weekend Rent",
      minWidth: 150,
    },
    {
      field: "discountAmount",
      headerName: "Discount Amount",
      minWidth: 140,
    },
    {
      field: "baseRentWithDiscount",
      headerName: "Base Rent with Discount",
      minWidth: 200,
    },
    {
      field: "source",
      headerName: "Source",
      minWidth: 140,
    },
  ];

  const lastColumns = [
    {
      field: "originalSource",
      headerName: "Original Source",
      minWidth: 160,
    },
    {
      field: "bookingGeneratedOn",
      headerName: "Booking Generated On",
      minWidth: 200,
    },
  ];

  const inquiryAssignedOn = {
    field: "inquiryAssignedOn",
    headerName: "Inquiry Assigned On",
    minWidth: 200,
  }
  const bookingStatus = {
    field: "bookingStatus",
    headerName: "Booking Status",
    minWidth: 180,
  };
  const agentColumn = {
    field: "assignedAgent",
    headerName: "Agent Name",
    minWidth: 180,
  };

  const inquiryAgeColumn = {
    field: "inquiryAgeInDays",
    headerName: "Inquiry Age (Days)",
    minWidth: 180,
  };

  const getColumns = () => {
    if (activeTab === "unassignedInquiry") {
      return [inquiryAgeColumn, ...baseColumns];
    }
    if (activeTab === "assignedInquiry") {
      return [agentColumn, inquiryAgeColumn, ...baseColumns, ...lastColumns, inquiryAssignedOn];
    }
    if (selectedSource) {
      return [bookingStatus, ...baseColumns, ...lastColumns];
    }
    return [agentColumn, bookingStatus, ...baseColumns, ...lastColumns, inquiryAssignedOn];
  };

  const getTableTitle = () => {
    if (selectedAgent) return `${getAgentName(selectedAgent)} Bookings`;
    if (selectedSource) return `${capsconversion(selectedSource)} Bookings`;
    if (activeTab === "unassignedInquiry") return "Unassigned Inquiries";
    if (activeTab === "assignedInquiry") return "Assigned Inquiries";
    if (activeTab === "todayBookings") return "Today's Bookings";
    if (activeTab === "overallBookings") return "Monthly Bookings";
    return "Bookings Report";
  };

  const handleDownloadReport = () => {
    const exportColumns = getColumns();
    const exportRows = tableData.map(mapTableRow);
    const escapeCsvValue = (value) => {
      if (value === undefined || value === null) return "";
      const stringValue = typeof value === "object" ? JSON.stringify(value) : String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const csvContent = [
      exportColumns.map((column) => escapeCsvValue(column.headerName || column.field)),
      ...exportRows.map((row) =>
        exportColumns.map((column) => escapeCsvValue(row[column.field])),
      ),
    ]
      .map((line) => line.join(","))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${getTableTitle().replace(/[^a-z0-9]+/gi, "_")}_${new Date()
      .toISOString()
      .split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { key: "unassignedInquiry", label: "Unassigned Inquiries" },
    { key: "assignedInquiry", label: "Assigned Inquiries" },
    { key: "todayBookings", label: "Today Bookings" },
    { key: "overallBookings", label: "Monthly Bookings" },
  ];

  // ACTIVE TAB UI
  const commonSx = (isActive) => ({
    p: 1.5,
    borderBottom: "1px solid #eee",
    cursor: "poLato",

    bgcolor: isActive ? "#043a60" : "transparent",
    color: isActive ? "#f5f5f5" : "inherit",

    "&:hover": {
      bgcolor: "#424a4f",
      color: "#fff",
    },
  });

  // Helper to format numbers with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0";
    return Number(num).toLocaleString("en-IN");
  };

  // Calculate combined totals
  const combinedTodayBookings = (dashboardData?.todaysBookingCount || 0) + (dashboardData?.todayMobileAndWebBookingCount || 0);
  const combinedTodayRevenue = Number(dashboardData?.totalRevenueOfAllToday || 0);
  const combinedMonthlyBookings = (dashboardData?.currentMonthBookingCount || 0) + (dashboardData?.currentMonthMobileAndWebBookingCount || 0);
  const combinedMonthlyRevenue = Number(dashboardData?.totalRevenueOfAllMonthly || 0);

  // Calculate deficit and surplus percentages
  const monthlyDeficit = Number(dashboardData?.monthlyDeficitOfAllToday || 0);
  const monthlyRevenue = Number(dashboardData?.totalRevenueOfAllMonthly || 0);
  const monthlyTotal = monthlyDeficit + monthlyRevenue;
  const monthlyDeficitPercent = monthlyTotal === 0 ? 0 : (monthlyDeficit / monthlyTotal) * 100;
  const monthlyRevenuePercent = monthlyTotal === 0 ? 0 : (monthlyRevenue / monthlyTotal) * 100;

  // UI
   useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) return <Loading />;
return (
  <Box p={2} sx={{ background: "#DFE7F1", minHeight: "100vh" }}>
    <div style={{ width: '100%', height: '100%', background: '#DFE7F1', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
      <div style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        display: 'flex',
        width: '100%'
      }}>
        <div style={{
          color: '#040406',
          fontSize: 28,
          fontFamily: 'Lato',
          fontWeight: '700',
          lineHeight: "28px",
          wordWrap: 'break-word'
        }}>
          Sales Dashboard
        </div>
        <Button variant="contained" onClick={getDashboardData}>
          Refresh
        </Button>
      </div> {/* Added missing closing tag */}
      
      <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'flex' }}>
        <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
          {/* Requires attention banner */}
          <div style={{ alignSelf: 'stretch', height: 56, paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FDF5E0', overflow: 'hidden', borderRadius: 16, outline: '1px #E8974C solid', outlineOffset: '-1px', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
            <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 16, display: 'flex' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 52 52">
                <path fill="#D57C06" d="M50.128 41.666 28.824 2.104c-1.51-2.805-5.532-2.805-7.044 0L.479 41.666a4 4 0 0 0 3.516 5.897h42.611a4 4 0 0 0 3.523-5.897Zm-24.825-.228a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Zm2.715-25.144-.718 15.25a2 2 0 0 1-4 0L22.584 16.3a2.716 2.716 0 0 1 2.688-2.836h.026a2.717 2.717 0 0 1 2.716 2.837l.005-.007Z" />
              </svg>
              <div style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                <div style={{ color: '#CD4F00', fontSize: 20, fontFamily: 'Lato', fontWeight: '700', wordWrap: 'break-word' }}>Requires attention</div>
              </div>
            </div>
            <div>
              <span style={{ color: '#141512', fontSize: 24, fontFamily: 'Lato', fontWeight: '400', wordWrap: 'break-word' }}>{dashboardData?.totalInqiryCount || 0} inquiries assigned •</span>
              <span style={{ color: '#141512', fontSize: 28, fontFamily: 'Lato', fontWeight: '700', wordWrap: 'break-word' }}> </span>
              <span style={{ color: '#FF0011', fontSize: 28, fontFamily: 'Lato', fontStyle: 'italic', fontWeight: '700', wordWrap: 'break-word' }}>{dashboardData?.totalInqiryUnAssignedCount || 0}</span>
              <span style={{ color: '#141512', fontSize: 28, fontFamily: 'Lato', fontWeight: '700', wordWrap: 'break-word' }}> </span>
              <span style={{ color: '#141512', fontSize: 24, fontFamily: 'Lato', fontWeight: '400', wordWrap: 'break-word' }}>pending</span>
            </div>
          </div>

          {/* Financial Overview Cards */}
          <div style={{ alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 16, display: 'inline-flex', flexWrap: 'wrap' }}>
            {/* Today's Financial Overview */}
            <div style={{ flex: '1 1 0', minWidth: '280px', alignSelf: 'stretch', paddingLeft: 8, paddingRight: 8, paddingTop: 24, paddingBottom: 24, background: '#FEFEFE', boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.24)', borderRadius: 16, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', gap: 24, display: 'inline-flex' }}>
              <div style={{ width: '100%', justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#040406', fontSize: 20, fontFamily: 'Lato', fontWeight: '700', textTransform: 'capitalize', lineHeight: "28px", wordWrap: 'break-word' }}>Today's Financial Overview</div>
              </div>
              <div style={{ flex: '1 1 0', paddingLeft: 16, paddingRight: 16, flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', display: 'flex', width: '100%' }}>
                <div style={{ width: '100%', justifyContent: 'flex-start', alignItems: 'center', gap: 24, display: 'inline-flex', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 0', minWidth: '180px', alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Today's Deficit</div>
                    <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                      <div style={{ color: '#EA2431', fontSize: 26, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.64, wordWrap: 'break-word' }}>₹</div>
                      <div style={{ flex: '1 1 0', color: '#EA2431', fontSize: 26, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.64, wordWrap: 'break-word' }}>{formatNumber(dashboardData?.totalDeficitOfAllToday || 0)}</div>
                    </div>
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: '180px', alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Today's Revenue</div>
                    <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                      <div style={{ color: '#28904E', fontSize: 26, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.64, wordWrap: 'break-word' }}>₹</div>
                      <div style={{ flex: '1 1 0', color: '#28904E', fontSize: 26, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.64, wordWrap: 'break-word' }}>{formatNumber(dashboardData?.totalRevenueOfAllToday || 0)}</div>
                    </div>
                  </div>
                </div>
                <div style={{ alignSelf: 'stretch', height: 8, background: '#00A158', borderRadius: 8, flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 8, display: 'flex', marginTop: 16 }}>
                  <div style={{ width: `${(() => {
                      const deficit = Number(dashboardData?.todaysDeficit || 0);
                      const revenue = Number(dashboardData?.todaysGeneratedRevenue || 0);
                      const total = deficit + revenue;
                      if (total === 0) return '0%';
                      return `${Math.min((deficit / total) * 100, 100)}%`;
                    })()}`, flex: '1 1 0', background: '#EA2431', borderTopLeftRadius: 8, borderBottomLeftRadius: 8, borderRight: '8px white solid' }} />
                </div>
                <div style={{ alignSelf: 'stretch', padding: 8, background: '#F9FAFB', borderRadius: 16, outline: '1px #E8E8EA solid', outlineOffset: '-1px', justifyContent: 'flex-start', alignItems: 'center', gap: 16, display: 'inline-flex', marginTop: 16 }}>
                  <div style={{ width: 204, padding: 8, background: '#E5F2E8', overflow: 'hidden', borderRadius: 10, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'flex' }}>
                    <svg width="24" height="16" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 28C6.28 28 0 21.72 0 14C0 6.28 6.28 0 14 0C21.72 0 28 6.28 28 14C28 21.72 21.72 28 14 28ZM14 2C7.38 2 2 7.38 2 14C2 20.62 7.38 26 14 26C20.62 26 26 20.62 26 14C26 7.38 20.62 2 14 2Z" fill="#058234" />
                      <g transform="translate(8, 6)">
                        <path d="M11 6H1C0.44 6 0 5.56 0 5C0 4.44 0.44 4 1 4H11C11.56 4 12 4.44 12 5C12 5.56 11.56 6 11 6ZM11 2H1C0.44 2 0 1.56 0 1C0 0.44 0.44 0 1 0H11C11.56 0 12 0.44 12 1C12 1.56 11.56 2 11 2ZM5 16C4.68 16 4.36 15.84 4.16 15.56L0.16 9.56C0.021902 9.33987 -0.0246983 9.07449 0.0301428 8.82049C0.0849839 8.56649 0.236924 8.34397 0.453539 8.20043C0.670154 8.05689 0.93429 8.00369 1.18958 8.05219C1.44488 8.10068 1.67111 8.24703 1.82 8.46L5.82 14.46C6.12 14.92 6 15.54 5.54 15.84C5.36 15.96 5.18 16 4.98 16H5Z" fill="#058234" />
                        <path d="M3 10H1C0.44 10 0 9.56 0 9C0 8.44 0.44 8 1 8H3C4.66 8 6 6.66 6 5C6 3.34 4.66 2 3 2H1C0.44 2 0 1.56 0 1C0 0.44 0.44 0 1 0H3C4.32608 0 5.59785 0.526784 6.53553 1.46447C7.47322 2.40215 8 3.67392 8 5C8 6.32608 7.47322 7.59785 6.53553 8.53553C5.59785 9.47322 4.32608 10 3 10Z" fill="#058234" />
                      </g>
                    </svg>
                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', textTransform: 'capitalize', lineHeight: "28px", wordWrap: 'break-word' }}>Today's Surplus</div>
                  </div>
                  <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#032DBA', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>{formatNumber(dashboardData?.todaySurplus || 0)}</div>
                </div>
              </div>
            </div>

            {/* Monthly Financial Overview */}
            <div style={{ flex: '1 1 0', minWidth: '280px', alignSelf: 'stretch', paddingLeft: 8, paddingRight: 8, paddingTop: 24, paddingBottom: 24, background: '#FEFEFE', borderTopLeftRadius: 8, borderTopRightRadius: 16, borderBottomRightRadius: 16, borderBottomLeftRadius: 8, borderLeft: '12px #058234 solid', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 24, display: 'inline-flex' }}>
              <div style={{ width: '100%', justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#040406', fontSize: 20, fontFamily: 'Lato', fontWeight: '700', textTransform: 'capitalize', lineHeight: "28px", wordWrap: 'break-word' }}>Monthly Financial Overview</div>
              </div>
              <div style={{ paddingLeft: 16, paddingRight: 16, flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', display: 'flex', width: '100%' }}>
                <div style={{ width: '100%', justifyContent: 'flex-start', alignItems: 'center', gap: 24, display: 'inline-flex', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 0', minWidth: '180px', alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Monthly Deficit</div>
                    <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                      <div style={{ color: '#EA2431', fontSize: 26, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.64, wordWrap: 'break-word' }}>₹</div>
                      <div style={{ flex: '1 1 0', color: '#EA2431', fontSize: 26, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.64, wordWrap: 'break-word' }}>{formatNumber(dashboardData?.monthlyDeficitOfAllToday || 0)}</div>
                    </div>
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: '180px', alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Monthly Revenue</div>
                    <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                      <div style={{ color: '#28904E', fontSize: 26, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.64, wordWrap: 'break-word' }}>₹</div>
                      <div style={{ flex: '1 1 0', color: '#28904E', fontSize: 26, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.64, wordWrap: 'break-word' }}>{formatNumber(dashboardData?.totalRevenueOfAllMonthly || 0)}</div>
                    </div>
                  </div>
                </div>
                <div style={{ alignSelf: 'stretch', height: 8, background: '#00A158', borderRadius: 8, flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 8, display: 'flex', marginTop: 16 }}>
                  <div style={{ width: `${Math.min(monthlyDeficitPercent, 100)}%`, flex: '1 1 0', background: '#EA2431', borderTopLeftRadius: 8, borderBottomLeftRadius: 8, borderRight: '8px white solid' }} />
                </div>
                <div style={{ alignSelf: 'stretch', padding: 8, background: '#F9FAFB', borderRadius: 16, outline: '1px #E8E8EA solid', outlineOffset: '-1px', justifyContent: 'flex-start', alignItems: 'center', gap: 16, display: 'inline-flex', marginTop: 16 }}>
                  <div style={{ width: 204, padding: 8, background: '#E5F2E8', overflow: 'hidden', borderRadius: 10, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'flex' }}>
                    <svg width="24" height="16" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 28C6.28 28 0 21.72 0 14C0 6.28 6.28 0 14 0C21.72 0 28 6.28 28 14C28 21.72 21.72 28 14 28ZM14 2C7.38 2 2 7.38 2 14C2 20.62 7.38 26 14 26C20.62 26 26 20.62 26 14C26 7.38 20.62 2 14 2Z" fill="#058234" />
                      <g transform="translate(8, 6)">
                        <path d="M11 6H1C0.44 6 0 5.56 0 5C0 4.44 0.44 4 1 4H11C11.56 4 12 4.44 12 5C12 5.56 11.56 6 11 6ZM11 2H1C0.44 2 0 1.56 0 1C0 0.44 0.44 0 1 0H11C11.56 0 12 0.44 12 1C12 1.56 11.56 2 11 2ZM5 16C4.68 16 4.36 15.84 4.16 15.56L0.16 9.56C0.021902 9.33987 -0.0246983 9.07449 0.0301428 8.82049C0.0849839 8.56649 0.236924 8.34397 0.453539 8.20043C0.670154 8.05689 0.93429 8.00369 1.18958 8.05219C1.44488 8.10068 1.67111 8.24703 1.82 8.46L5.82 14.46C6.12 14.92 6 15.54 5.54 15.84C5.36 15.96 5.18 16 4.98 16H5Z" fill="#058234" />
                        <path d="M3 10H1C0.44 10 0 9.56 0 9C0 8.44 0.44 8 1 8H3C4.66 8 6 6.66 6 5C6 3.34 4.66 2 3 2H1C0.44 2 0 1.56 0 1C0 0.44 0.44 0 1 0H3C4.32608 0 5.59785 0.526784 6.53553 1.46447C7.47322 2.40215 8 3.67392 8 5C8 6.32608 7.47322 7.59785 6.53553 8.53553C5.59785 9.47322 4.32608 10 3 10Z" fill="#058234" />
                      </g>
                    </svg>
                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', textTransform: 'capitalize', lineHeight: "28px", wordWrap: 'break-word' }}>Monthly Surplus</div>
                  </div>
                  <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#032DBA', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>{formatNumber(dashboardData?.monthlySurplus || 0)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* NEW SECTION: Till Today Target & Metrics */}
          <div style={{ alignSelf: 'stretch', marginTop: 16 }}>
            <div style={{ 
              alignSelf: 'stretch', 
              height: 160, 
              background: 'white', 
              borderRadius: 12, 
              boxShadow: '0px 6px 18px -6px rgba(0,0,0,0.05)', 
              outline: '1px solid #E5E7EB', 
              outlineOffset: '-1px',
              display: 'inline-flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              overflow: 'hidden',
              width: '100%'
            }}>
              {/* Left Column - Till Today Target */}
              <div style={{ 
                width: 192, 
                alignSelf: 'stretch', 
                padding: '16px 16px', 
                background: '#F9FAFB', 
                borderRight: '1px solid #E5E7EB',
                display: 'inline-flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <div style={{ 
                  alignSelf: 'stretch', 
                  height: 16, 
                  justifyContent: 'flex-start',
                  color: '#1F2937',
                  fontSize: 14,
                  fontFamily: 'Instrument Sans',
                  fontWeight: '400',
                  lineHeight: '28px'
                }}>
                  TILL TODAY TARGET
                </div>
                <div style={{ 
                  alignSelf: 'stretch', 
                  height: 80, 
                  justifyContent: 'center',
                  color: '#111827',
                  fontSize: 24,
                  fontFamily: 'Inter',
                  fontWeight: '800',
                  lineHeight: '32px'
                }}>
                  ₹{formatNumber(dashboardData?.tillTodayTarget || 0)}
                </div>
              </div>

              {/* Right Columns - Metrics Grid */}
              <div style={{ 
                flex: 1, 
                alignSelf: 'stretch', 
                display: 'inline-flex', 
                flexDirection: 'column', 
                justifyContent: 'flex-start', 
                alignItems: 'flex-start'
              }}>
                {/* Row 1 */}
                <div style={{ 
                  alignSelf: 'stretch', 
                  height: 80, 
                  display: 'inline-flex', 
                  justifyContent: 'flex-start', 
                  alignItems: 'flex-start'
                }}>
                  {/* Till Today Revenue */}
                  <div style={{ 
                    flex: 1, 
                    alignSelf: 'stretch', 
                    padding: '14px 16px', 
                    borderRight: '1px solid #F3F4F6', 
                    borderBottom: '1px solid #F3F4F6',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '6px'
                  }}>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      height: 16, 
                      justifyContent: 'flex-start',
                      color: '#1F2937',
                      fontSize: 14,
                      fontFamily: 'Instrument Sans',
                      fontWeight: '400',
                      lineHeight: '28px'
                    }}>
                      Till Today Revenue
                    </div>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      justifyContent: 'flex-start',
                      color: '#10B981',
                      fontSize: 16,
                      fontFamily: 'Inter',
                      fontWeight: '800'
                    }}>
                      ₹{formatNumber(dashboardData?.tillTodayRevenueGenerated || 0)}
                    </div>
                  </div>

                  {/* Till Today Deficit */}
                  <div style={{ 
                    flex: 1, 
                    alignSelf: 'stretch', 
                    padding: '14px 16px', 
                    borderRight: '1px solid #F3F4F6', 
                    borderBottom: '1px solid #F3F4F6',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '6px'
                  }}>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      height: 16, 
                      justifyContent: 'flex-start',
                      color: '#1F2937',
                      fontSize: 14,
                      fontFamily: 'Instrument Sans',
                      fontWeight: '400',
                      lineHeight: '28px'
                    }}>
                      Till Today Deficit
                    </div>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      justifyContent: 'flex-start',
                      color: '#EF4444',
                      fontSize: 16,
                      fontFamily: 'Inter',
                      fontWeight: '800'
                    }}>
                      ₹{formatNumber(dashboardData?.tillTodayDeficit || 0)}
                    </div>
                  </div>

                  {/* Daily Run Rate */}
                  <div style={{ 
                    flex: 1, 
                    alignSelf: 'stretch', 
                    padding: '14px 16px', 
                    borderBottom: '1px solid #F3F4F6',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '6px'
                  }}>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      height: 16, 
                      justifyContent: 'flex-start',
                      color: '#1F2937',
                      fontSize: 14,
                      fontFamily: 'Instrument Sans',
                      fontWeight: '400',
                      lineHeight: '28px'
                    }}>
                      Daily Run Rate
                    </div>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      justifyContent: 'flex-start',
                      color: '#111827',
                      fontSize: 16,
                      fontFamily: 'Inter',
                      fontWeight: '800'
                    }}>
                      ₹{formatNumber(dashboardData?.dailyRunRate || 0)}
                    </div>
                  </div>
                </div>

                {/* Row 2 */}
                <div style={{ 
                  alignSelf: 'stretch', 
                  height: 80, 
                  display: 'inline-flex', 
                  justifyContent: 'flex-start', 
                  alignItems: 'flex-start'
                }}>
                  {/* Projected Monthly Revenue */}
                  <div style={{ 
                    flex: 1, 
                    alignSelf: 'stretch', 
                    padding: '14px 16px', 
                    borderRight: '1px solid #F3F4F6',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '6px'
                  }}>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      height: 16, 
                      justifyContent: 'flex-start',
                      color: '#1F2937',
                      fontSize: 14,
                      fontFamily: 'Instrument Sans',
                      fontWeight: '400',
                      lineHeight: '28px'
                    }}>
                      Proj. Monthly Revenue
                    </div>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      justifyContent: 'flex-start',
                      color: '#10B981',
                      fontSize: 16,
                      fontFamily: 'Inter',
                      fontWeight: '800'
                    }}>
                      ₹{formatNumber(dashboardData?.projectedMonthlyRevenue || 0)}
                    </div>
                  </div>

                  {/* Run Rate Monthly Deficit */}
                  <div style={{ 
                    flex: 1, 
                    alignSelf: 'stretch', 
                    padding: '14px 16px', 
                    borderRight: '1px solid #F3F4F6',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '6px'
                  }}>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      height: 16, 
                      justifyContent: 'flex-start',
                      color: '#1F2937',
                      fontSize: 14,
                      fontFamily: 'Instrument Sans',
                      fontWeight: '400',
                      lineHeight: '28px'
                    }}>
                      Run Rate Monthly Deficit
                    </div>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      justifyContent: 'flex-start',
                      color: '#EF4444',
                      fontSize: 16,
                      fontFamily: 'Inter',
                      fontWeight: '800'
                    }}>
                      ₹{formatNumber(dashboardData?.runRateMonthlyDeficit || 0)}
                    </div>
                  </div>

                  {/* Required Daily Run Rate */}
                  <div style={{ 
                    flex: 1, 
                    alignSelf: 'stretch', 
                    padding: '14px 16px',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '6px'
                  }}>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      height: 16, 
                      justifyContent: 'flex-start',
                      color: '#1F2937',
                      fontSize: 14,
                      fontFamily: 'Instrument Sans',
                      fontWeight: '400',
                      lineHeight: '28px'
                    }}>
                      Required Daily Run Rate
                    </div>
                    <div style={{ 
                      alignSelf: 'stretch', 
                      justifyContent: 'flex-start',
                      color: '#EF4444',
                      fontSize: 16,
                      fontFamily: 'Inter',
                      fontWeight: '800'
                    }}>
                      ₹{formatNumber(dashboardData?.requiredDailyRunRate || 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Channel Stats Cards */}
        <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 16, display: 'inline-flex', flexWrap: 'wrap' }}>
          {/* Admin Channel - UPDATED WITH CORRECT FIELD NAMES */}
          <div style={{ flex: '1 1 0', minWidth: '280px', height: 'auto', padding: 16, background: '#FEFEFE', borderRadius: 16, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex' }}>
            <div style={{ alignSelf: 'stretch', paddingTop: 8, paddingBottom: 8, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
              <div style={{ width: 44, height: 44, padding: 8, overflow: 'hidden', borderRadius: 8, justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path fill="#032DBA" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <div style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#040406', fontSize: 20, fontFamily: 'Lato', fontWeight: '700', textTransform: 'capitalize', lineHeight: "28px", wordWrap: 'break-word' }}>Admin</div>
                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#808080', fontSize: 14, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Direct Bookings</div>
              </div>
            </div>
            <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 12, display: 'flex' }}>
              {/* Today Section */}
              <div style={{ width: '100%', borderRadius: 16, border: '2px solid #D5D5D5', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex' }}>
                <div style={{ alignSelf: 'stretch', height: 42, paddingLeft: 16, paddingRight: 16, background: '#EAEAEA', boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.24) inset', borderTopLeftRadius: 16, borderTopRightRadius: 16, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                  <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#040406', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Today</div>
                </div>
                <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'inline-flex', flexWrap: 'wrap' }}>
                  {/* Today Bookings */}
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Bookings</div>
                      <div style={{ alignSelf: 'stretch', color: '#032DBA', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                        {dashboardData?.todaysBookingCount || 0}
                      </div>
                    </div>
                  </div>
                  {/* Today Revenue */}
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Revenue</div>
                      <div style={{ alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                        <div style={{ color: '#058234', fontSize: 22, fontFamily: 'Sahitya', fontWeight: '700', letterSpacing: 0.44, wordWrap: 'break-word' }}>₹</div>
                        <div style={{ flex: '1 1 0', color: '#058234', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                          {formatNumber(dashboardData?.todaysGeneratedRevenue || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Divider */}
              <div style={{ width: '80%', height: 0, outline: '1px #B1B1B1 solid', outlineOffset: '-0.50px' }}></div>
              
              {/* Monthly Section */}
              <div style={{ width: '100%', borderRadius: 16, border: '2px solid #D5D5D5', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex' }}>
                <div style={{ alignSelf: 'stretch', height: 42, paddingLeft: 16, paddingRight: 16, background: '#EAEAEA', boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.24) inset', borderTopLeftRadius: 16, borderTopRightRadius: 16, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                  <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#040406', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Monthly</div>
                </div>
                <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'inline-flex', flexWrap: 'wrap' }}>
                  {/* Monthly Bookings */}
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Bookings</div>
                      <div style={{ alignSelf: 'stretch', color: '#032DBA', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                        {dashboardData?.currentMonthBookingCount || 0}
                      </div>
                    </div>
                  </div>
                  {/* Monthly Revenue */}
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Revenue</div>
                      <div style={{ alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                        <div style={{ color: '#058234', fontSize: 22, fontFamily: 'Sahitya', fontWeight: '700', letterSpacing: 0.44, wordWrap: 'break-word' }}>₹</div>
                        <div style={{ flex: '1 1 0', color: '#058234', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                          {formatNumber(dashboardData?.currentMonthTotalRevenue || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile/Web Channel */}
          <div style={{ flex: '1 1 0', minWidth: '280px', height: 'auto', padding: 16, background: '#FEFEFE', borderRadius: 16, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex' }}>
            <div style={{ alignSelf: 'stretch', paddingTop: 8, paddingBottom: 8, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
              <div style={{ width: 44, height: 44, padding: 8, overflow: 'hidden', borderRadius: 8, justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path stroke="#032DBA" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19a9 9 0 0 0 0-18m0 18a9 9 0 0 1 0-18m0 18c2.761 0 3.941-5.163 3.941-9 0-3.837-1.18-9-3.941-9m0 18c-2.761 0-3.941-5.163-3.941-9 0-3.837 1.18-9 3.941-9M1.5 7h17m-17 6h17" />
                </svg>
              </div>
              <div style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#040406', fontSize: 20, fontFamily: 'Lato', fontWeight: '700', textTransform: 'capitalize', lineHeight: "28px", wordWrap: 'break-word' }}>Mobile/ Web</div>
                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#808080', fontSize: 14, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>App & website</div>
              </div>
            </div>
            <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 12, display: 'flex' }}>
              <div style={{ width: '100%', borderRadius: 16, border: '2px solid #D5D5D5', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex' }}>
                <div style={{ alignSelf: 'stretch', height: 42, paddingLeft: 16, paddingRight: 16, background: '#EAEAEA', boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.24) inset', borderTopLeftRadius: 16, borderTopRightRadius: 16, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                  <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#040406', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Today</div>
                </div>
                <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'inline-flex', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Bookings</div>
                      <div style={{ alignSelf: 'stretch', color: '#032DBA', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                        {dashboardData?.todayMobileAndWebBookingCount || 0}
                      </div>
                    </div>
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Revenue</div>
                      <div style={{ alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                        <div style={{ color: '#058234', fontSize: 22, fontFamily: 'Sahitya', fontWeight: '700', letterSpacing: 0.44, wordWrap: 'break-word' }}>₹</div>
                        <div style={{ flex: '1 1 0', color: '#058234', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                          {formatNumber(dashboardData?.todayMobileAndWebGeneratedRevenue || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ width: '80%', height: 0, outline: '1px #B1B1B1 solid', outlineOffset: '-0.50px' }}></div>
              <div style={{ width: '100%', borderRadius: 16, border: '2px solid #D5D5D5', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex' }}>
                <div style={{ alignSelf: 'stretch', height: 42, paddingLeft: 16, paddingRight: 16, background: '#EAEAEA', boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.24) inset', borderTopLeftRadius: 16, borderTopRightRadius: 16, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                  <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: '#040406', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Monthly</div>
                </div>
                <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'inline-flex', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Bookings</div>
                      <div style={{ alignSelf: 'stretch', color: '#032DBA', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                        {dashboardData?.currentMonthMobileAndWebBookingCount || 0}
                      </div>
                    </div>
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Revenue</div>
                      <div style={{ alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                        <div style={{ color: '#058234', fontSize: 22, fontFamily: 'Sahitya', fontWeight: '700', letterSpacing: 0.44, wordWrap: 'break-word' }}>₹</div>
                        <div style={{ flex: '1 1 0', color: '#058234', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                          {formatNumber(dashboardData?.currentMonthMobileAndWebGeneratedRevenue || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Combined Total */}
          <div style={{ flex: '1 1 0', minWidth: '280px', height: 'auto', padding: 16, background: '#FEFEFE', borderRadius: 16, outline: '1px #032DBA solid', outlineOffset: '-1px', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex' }}>
            <div style={{ alignSelf: 'stretch', paddingTop: 8, paddingBottom: 8, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
              <div style={{ width: 44, height: 44, padding: 8, overflow: 'hidden', borderRadius: 8, justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path fill="#032DBA" fillRule="evenodd" d="M8 .2v2.052A8 8 0 1 0 17.748 12H19.8c-.927 4.564-4.962 8-9.8 8-5.523 0-10-4.477-10-10C0 5.162 3.435 1.127 8 .2m2-.2c5.468 0 9.91 4.388 9.999 9.835L20 10H10V0Z" clipRule="evenodd" />
                </svg>
              </div>
              <div style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#040406', fontSize: 20, fontFamily: 'Lato', fontWeight: '700', textTransform: 'capitalize', lineHeight: "28px", wordWrap: 'break-word' }}>Combined Total</div>
                <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#808080', fontSize: 14, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Admin + Mobile/Web</div>
              </div>
            </div>
            <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 12, display: 'flex' }}>
              <div style={{ width: '100%', borderRadius: 16, border: '2px solid #D5D5D5', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex' }}>
                <div style={{ alignSelf: 'stretch', height: 42, paddingLeft: 16, paddingRight: 16, background: '#747EFF', boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.24) inset', borderTopLeftRadius: 16, borderTopRightRadius: 16, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                  <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: 'white', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Today</div>
                </div>
                <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'inline-flex', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Bookings</div>
                      <div style={{ alignSelf: 'stretch', color: '#032DBA', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>{combinedTodayBookings}</div>
                    </div>
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Revenue</div>
                      <div style={{ alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                        <div style={{ color: '#058234', fontSize: 22, fontFamily: 'Sahitya', fontWeight: '700', letterSpacing: 0.44, wordWrap: 'break-word' }}>₹</div>
                        <div style={{ flex: '1 1 0', color: '#058234', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>{formatNumber(combinedTodayRevenue)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ width: '80%', height: 0, outline: '1px #B1B1B1 solid', outlineOffset: '-0.50px' }}></div>
              <div style={{ width: '100%', borderRadius: 16, border: '2px solid #D5D5D5', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex' }}>
                <div style={{ alignSelf: 'stretch', height: 42, paddingLeft: 16, paddingRight: 16, background: '#747EFF', boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.24) inset', borderTopLeftRadius: 16, borderTopRightRadius: 16, justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                  <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', justifyContent: 'center', display: 'flex', flexDirection: 'column', color: 'white', fontSize: 18, fontFamily: 'Lato', fontWeight: '400', lineHeight: "28px", wordWrap: 'break-word' }}>Monthly</div>
                </div>
                <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'inline-flex', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'center', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Bookings</div>
                      <div style={{ alignSelf: 'stretch', color: '#032DBA', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>{combinedMonthlyBookings}</div>
                    </div>
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: '100px', alignSelf: 'stretch', paddingLeft: 16, paddingRight: 16, paddingTop: 8, paddingBottom: 8, background: '#FEFEFE', boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16) inset', borderBottomRightRadius: 8, borderBottomLeftRadius: 8, outline: '1px #E2E2E2 solid', outlineOffset: '-1px', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    <div style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 24, display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#323433', fontSize: 16, fontFamily: 'Lato', fontWeight: '700', lineHeight: "28px", wordWrap: 'break-word' }}>Revenue</div>
                      <div style={{ alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                        <div style={{ color: '#058234', fontSize: 22, fontFamily: 'Sahitya', fontWeight: '700', letterSpacing: 0.44, wordWrap: 'break-word' }}>₹</div>
                        <div style={{ flex: '1 1 0', color: '#058234', fontSize: 24, fontFamily: 'Lato', fontWeight: '700', letterSpacing: 0.48, wordWrap: 'break-word' }}>{formatNumber(combinedMonthlyRevenue)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

{/* New Accordion & Grid Layout */}
      <Box display="flex" gap="20px" mt={4} sx={{ background: 'transparent' }}>
        
        {/* LEFT PANEL - Sidebar Controls */}
        <Paper sx={{ width: 274, p: "16px", height: "fit-content", background: "white", borderRadius: "16px", display: "flex", flexDirection: "column", gap: "24px", boxShadow: "none" }}>
          
          {/* ACCORDION 1: Bookings Details */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div 
              onClick={() => toggleAccordion('bookingsDetails')}
              style={{ alignSelf: 'stretch', padding: 8, background: '#E0EEFF', borderTopLeftRadius: 8, borderTopRightRadius: 8, borderBottom: '1px #BEBEBE solid', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', cursor: 'pointer' }}
            >
              <div style={{ color: '#808080', fontSize: 16, fontFamily: 'Instrument Sans', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.32 }}>Bookings details</div>
              <ExpandMoreIcon style={{ color: '#323433', transform: expandedAccordions.bookingsDetails ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.3s' }} />
            </div>
            {expandedAccordions.bookingsDetails && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Inquiries Toggles */}
              <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ color: '#2C2C2C', fontSize: 18, fontFamily: 'Instrument Sans', fontWeight: '600' }}>Inquiries</div>
                <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ color: '#A8A8A8', fontSize: 14, fontFamily: 'Instrument Sans', fontWeight: '400' }}>
                    {activeTab === "unassignedInquiry" ? "Unassigned Inquiries" : activeTab === "assignedInquiry" ? "Assigned Inquiries" : ""}
                  </div>
                  <div style={{ alignSelf: 'stretch', padding: 4, display: 'inline-flex', position: 'relative', borderRadius: 16, outline: '1px #E5E5E5 solid', height: 48, alignItems: 'center' }}>
                    <div 
                      onClick={() => { setActiveTab("unassignedInquiry"); setSelectedAgent(""); setSelectedSource(""); }}
                      style={{ flex: 1, height: 40, borderRadius: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', background: activeTab === "unassignedInquiry" ? '#286DC3' : 'transparent', zIndex: 2 }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path fill={activeTab === "unassignedInquiry" ? "white" : "#505050"} d="M8 0C6.93913 0 5.92172 0.421427 5.17157 1.17157C4.42143 1.92172 4 2.93913 4 4C4 5.06087 4.42143 6.07828 5.17157 6.82843C5.92172 7.57857 6.93913 8 8 8C9.06087 8 10.0783 7.57857 10.8284 6.82843C11.5786 6.07828 12 5.06087 12 4C12 2.93913 11.5786 1.92172 10.8284 1.17157C10.0783 0.421427 9.06087 0 8 0ZM8 2C8.53043 2 9.03914 2.21071 9.41421 2.58579C9.78929 2.96086 10 3.46957 10 4C10 4.53043 9.78929 5.03914 9.41421 5.41421C9.03914 5.78929 8.53043 6 8 6C7.46957 6 6.96086 5.78929 6.58579 5.41421C6.21071 5.03914 6 4.53043 6 4C6 3.46957 6.21071 2.96086 6.58579 2.58579C6.96086 2.21071 7.46957 2 8 2ZM8 9C5.33 9 0 10.33 0 13V16H9.5C9.24833 15.394 9.08987 14.7534 9.03 14.1H1.9V13C1.9 12.36 5.03 10.9 8 10.9C8.5 10.9 9 10.95 9.5 11.03C9.7566 10.3985 10.1109 9.81136 10.55 9.29C9.61 9.1 8.71 9 8 9ZM15.5 9C13 9 11 11 11 13.5C11 16 13 18 15.5 18C18 18 20 16 20 13.5C20 11 18 9 15.5 9ZM15.5 10.5C17.16 10.5 18.5 11.84 18.5 13.5C18.5 14.06 18.35 14.58 18.08 15L14 10.92C14.42 10.65 14.94 10.5 15.5 10.5ZM12.92 12L17 16.08C16.58 16.35 16.06 16.5 15.5 16.5C13.84 16.5 12.5 15.16 12.5 13.5C12.5 12.94 12.65 12.42 12.92 12Z"/>
                      </svg>
                    </div>
                    <div 
                      onClick={() => { setActiveTab("assignedInquiry"); setSelectedAgent(""); setSelectedSource(""); }}
                      style={{ flex: 1, height: 40, borderRadius: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', background: activeTab === "assignedInquiry" ? '#286DC3' : 'transparent', zIndex: 2 }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path fill={activeTab === "assignedInquiry" ? "white" : "#505050"} d="M18.1 8.5L19.5 9.91L12.97 16.5L9.5 13L10.9 11.59L12.97 13.67L18.1 8.5ZM8 0C9.06087 0 10.0783 0.421427 10.8284 1.17157C11.5786 1.92172 12 2.93913 12 4C12 5.06087 11.5786 6.07828 10.8284 6.82843C10.0783 7.57857 9.06087 8 8 8C6.93913 8 5.92172 7.57857 5.17157 6.82843C4.42143 6.07828 4 5.06087 4 4C4 2.93913 4.42143 1.92172 5.17157 1.17157C5.92172 0.421427 6.93913 0 8 0ZM8 2C7.46957 2 6.96086 2.21071 6.58579 2.58579C6.21071 2.96086 6 3.46957 6 4C6 4.53043 6.21071 5.03914 6.58579 5.41421C6.96086 5.78929 7.46957 6 8 6C8.53043 6 9.03914 5.78929 9.41421 5.41421C9.78929 5.03914 10 4.53043 10 4C10 3.46957 9.78929 2.96086 9.41421 2.58579C9.03914 2.21071 8.53043 2 8 2ZM8 9C8.68 9 9.5 9.09 10.41 9.26L8.74 10.93L8 10.9C5.03 10.9 1.9 12.36 1.9 13V14.1H8.1L10 16H0V13C0 10.34 5.33 9 8 9Z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ borderBottom: '0.8px #EAEAEA solid' }}></div>

              {/* Bookings Toggles */}
              <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ color: '#2C2C2C', fontSize: 18, fontFamily: 'Instrument Sans', fontWeight: '600' }}>Admin's Bookings</div>
                <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ color: '#A8A8A8', fontSize: 14, fontFamily: 'Instrument Sans', fontWeight: '400' }}>
                    {activeTab === "todayBookings" ? "Today's Bookings" : activeTab === "overallBookings" ? "Monthly Bookings" : ""}
                  </div>
                  <div style={{ alignSelf: 'stretch', padding: 4, display: 'inline-flex', position: 'relative', borderRadius: 16, outline: '1px #E5E5E5 solid', height: 48, alignItems: 'center' }}>
                    <div 
                      onClick={() => { setActiveTab("todayBookings"); setSelectedAgent(""); setSelectedSource(""); }}
                      style={{ flex: 1, height: 40, borderRadius: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', background: activeTab === "todayBookings" ? '#286DC3' : 'transparent', zIndex: 2 }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path fill={activeTab === "todayBookings" ? "white" : "#505050"} d="M4.225 13.775C3.74167 13.2917 3.5 12.7 3.5 12C3.5 11.3 3.74167 10.7083 4.225 10.225C4.70833 9.74167 5.3 9.5 6 9.5C6.7 9.5 7.29167 9.74167 7.775 10.225C8.25833 10.7083 8.5 11.3 8.5 12C8.5 12.7 8.25833 13.2917 7.775 13.775C7.29167 14.2583 6.7 14.5 6 14.5C5.3 14.5 4.70833 14.2583 4.225 13.775ZM2 20C1.45 20 0.979333 19.8043 0.588 19.413C0.196667 19.0217 0.000666667 18.5507 0 18V4C0 3.45 0.196 2.97934 0.588 2.588C0.98 2.19667 1.45067 2.00067 2 2H3V1C3 0.71667 3.096 0.479337 3.288 0.288004C3.48 0.0966702 3.71733 0.000670115 4 3.44827e-06C4.28267 -0.000663218 4.52033 0.0953369 4.713 0.288004C4.90567 0.48067 5.00133 0.718003 5 1V2H13V1C13 0.71667 13.096 0.479337 13.288 0.288004C13.48 0.0966702 13.7173 0.000670115 14 3.44827e-06C14.2827 -0.000663218 14.5203 0.0953369 14.713 0.288004C14.9057 0.48067 15.0013 0.718003 15 1V2H16C16.55 2 17.021 2.196 17.413 2.588C17.805 2.98 18.0007 3.45067 18 4V18C18 18.55 17.8043 19.021 17.413 19.413C17.0217 19.805 16.5507 20.0007 16 20H2ZM2 18H16V8H2V18ZM2 6H16V4H2V6Z"/>
                      </svg>
                    </div>
                    <div 
                      onClick={() => { setActiveTab("overallBookings"); setSelectedAgent(""); setSelectedSource(""); }}
                      style={{ flex: 1, height: 40, borderRadius: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', background: activeTab === "overallBookings" ? '#286DC3' : 'transparent', zIndex: 2 }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path fill={activeTab === "overallBookings" ? "white" : "#505050"} d="M16 2H15V0H13V2H5V0H3V2H2C0.89 2 0.00999999 2.9 0.00999999 4L0 18C0 18.5304 0.210714 19.0391 0.585786 19.4142C0.960859 19.7893 1.46957 20 2 20H16C17.1 20 18 19.1 18 18V4C18 2.9 17.1 2 16 2ZM16 18H2V8H16V18ZM16 6H2V4H16V6ZM6 12H4V10H6V12ZM10 12H8V10H10V12ZM14 12H12V10H14V12ZM6 16H4V14H6V16ZM10 16H8V14H10V16ZM14 16H12V14H14V16Z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* ACCORDION 2: My Teammates */}
<div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
  <div 
    onClick={() => toggleAccordion('teammates')}
    style={{ alignSelf: 'stretch', padding: 8, borderBottom: '1px #BEBEBE solid', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', cursor: 'pointer' }}
  >
    <div style={{ color: '#808080', fontSize: 16, fontFamily: 'Instrument Sans', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.32 }}>My teammates</div>
    <ExpandMoreIcon style={{ color: '#323433', transform: expandedAccordions.teammates ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.3s' }} />
  </div>
  {expandedAccordions.teammates && (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '240px', overflowY: 'auto' }}>
      {agentsList?.map((agent) => {
        const isSelected = !selectedSource && selectedAgent === agent?.id;
        return (
          <div 
            key={agent.id}
            onClick={() => {
              setSelectedAgent(prev => prev === agent.id ? "" : agent.id);
              setSelectedSource("");
              setActiveTab("");
              setAgentTab("todayBookings");
            }}
            style={{ alignSelf: 'stretch', padding: '10px 16px', borderRadius: 8, justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'inline-flex', cursor: 'pointer', background: isSelected ? '#E0EEFF' : 'transparent' }}
          >
            <div style={{ width: 32, height: 32, background: '#EEF2FF', borderRadius: 32, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ color: '#032DBA', fontSize: 12, fontFamily: 'Instrument Sans', fontWeight: '700' }}>{getInitials(agent?.name)}</div>
            </div>
            <div style={{ color: '#2C2C2C', fontSize: 16, fontFamily: 'Instrument Sans', fontWeight: isSelected ? '600' : '500', textTransform: 'capitalize' }}>{agent?.name}</div>
          </div>
        );
      })}
    </div>
  )}
</div>

          {/* ACCORDION 3: Source */}
<div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
  <div 
    onClick={() => toggleAccordion('source')}
    style={{ alignSelf: 'stretch', padding: 8, borderBottom: '1px #BEBEBE solid', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', cursor: 'pointer' }}
  >
    <div style={{ color: '#808080', fontSize: 16, fontFamily: 'Instrument Sans', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.32 }}>Source</div>
    <ExpandMoreIcon style={{ color: '#323433', transform: expandedAccordions.source ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.3s' }} />
  </div>
  {expandedAccordions.source && (
    <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ color: '#2C2C2C', fontSize: 18, fontFamily: 'Instrument Sans', fontWeight: '600' }}>Source of booking</div>
      <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ color: '#A8A8A8', fontSize: 14, fontFamily: 'Instrument Sans', fontWeight: '400' }}>
          {selectedSource === "website" ? "Website" : selectedSource === "mobileapp" ? "App" : "Select Source"}
        </div>
        <div style={{ alignSelf: 'stretch', padding: 4, display: 'inline-flex', position: 'relative', borderRadius: 16, outline: '1px #E5E5E5 solid', height: 48, alignItems: 'center' }}>
          <div 
            onClick={() => {
              setSelectedSource(prev => prev === "website" ? "" : "website");
              setSelectedAgent("");
              setActiveTab("");
              setAgentTab("todayBookings");
            }}
            style={{ flex: 1, height: 40, borderRadius: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', background: (!selectedAgent && selectedSource === "website") ? '#286DC3' : 'transparent', zIndex: 2 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M10 19C11.1819 19 12.3522 18.7672 13.4442 18.3149C14.5361 17.8626 15.5282 17.1997 16.364 16.364C17.1997 15.5282 17.8626 14.5361 18.3149 13.4442C18.7672 12.3522 19 11.1819 19 10C19 8.8181 18.7672 7.64778 18.3149 6.55585C17.8626 5.46392 17.1997 4.47177 16.364 3.63604C15.5282 2.80031 14.5361 2.13738 13.4442 1.68508C12.3522 1.23279 11.1819 1 10 1M10 19C8.8181 19 7.64778 18.7672 6.55585 18.3149C5.46392 17.8626 4.47177 17.1997 3.63604 16.364C2.80031 15.5282 2.13738 14.5361 1.68508 13.4442C1.23279 12.3522 1 11.1819 1 10C1 8.8181 1.23279 7.64778 1.68508 6.55585C2.13738 5.46392 2.80031 4.47177 3.63604 3.63604C4.47177 2.80031 5.46392 2.13738 6.55585 1.68508C7.64778 1.23279 8.8181 1 10 1M10 19C12.761 19 13.941 13.837 13.941 10C13.941 6.163 12.761 1 10 1M10 19C7.239 19 6.059 13.837 6.059 10C6.059 6.163 7.239 1 10 1M1.5 7H18.5M1.5 13H18.5" stroke={(!selectedAgent && selectedSource === "website") ? "white" : "#505050" } stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
          </div>
          <div 
            onClick={() => {
              setSelectedSource(prev => prev === "mobileapp" ? "" : "mobileapp");
              setSelectedAgent("");
              setActiveTab("");
              setAgentTab("todayBookings");
            }}
            style={{ flex: 1, height: 40, borderRadius: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', background: (!selectedAgent && selectedSource === "mobileapp") ? '#286DC3' : 'transparent', zIndex: 2 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="20" viewBox="0 0 14 20" fill="none">
<path d="M5.25 3.75H8.25M11.036 0.75H2.464C1.518 0.75 0.75 1.556 0.75 2.55V16.95C0.75 17.944 1.518 18.75 2.464 18.75H11.036C11.983 18.75 12.75 17.944 12.75 16.95V2.55C12.75 1.556 11.983 0.75 11.036 0.75Z" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" stroke={(!selectedAgent && selectedSource === "mobileapp") ? "white" : "#505050"}/></svg>
          </div>
        </div>
      </div>
    </div>
  )}
</div>
        </Paper>

        {/* RIGHT PANEL - Main Data Presentation Container */}
        <Box flex={1} style={{ padding: 16, background: 'white', borderRadius: 16, display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
          
          {/* Contextual Agent view sub-tabs details header row setup */}
          {(selectedAgent || selectedSource) && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ fontFamily: 'Instrument Sans', fontWeight: '700', color: '#121212' }}>
                  {selectedAgent ? `${getAgentName(selectedAgent)}` : `${ capsconversion(selectedSource)} Bookings`}
                </Typography>
                
                
                {/* Agent/Source Dashboard Inner Tabs Controls */}
                <Box display="flex" gap="8px" p="4px" sx={{ background: '#F5F5F5', borderRadius: '12px' }}>
                  <Button 
                    onClick={() => setAgentTab("todayBookings")}
                    variant={agentTab === "todayBookings" ? "contained" : "text"}
                    sx={{ borderRadius: '8px', textTransform: 'capitalize', boxShadow: 'none', bgcolor: agentTab === "todayBookings" ? '#286DC3' : 'transparent' }}
                  >
                    Today
                  </Button>
                  <Button 
                    onClick={() => setAgentTab("monthlyBookings")}
                    variant={agentTab === "monthlyBookings" ? "contained" : "text"}
                    sx={{ borderRadius: '8px', textTransform: 'capitalize', boxShadow: 'none', bgcolor: agentTab === "monthlyBookings" ? '#286DC3' : 'transparent' }}
                  >
                    Monthly
                  </Button>
                </Box>
              </Box>

              {/* Status Visual Grid Panels */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full">
                <div style={{ background: '#F9FAFB', border: '1px solid #EAEAEA', borderRadius: 12, padding: 16 }}>
                  <h3 style={{ color: '#808080', fontSize: 14, fontWeight: '600' }}>Today Bookings</h3>
                  <p style={{ color: '#286DC3', fontSize: 24, fontWeight: '700', marginTop: 8 }}>
                    {isAgent
                      ? (dashboardData?.todaysGroupByAgentWiseBookings?.[String(selectedAgent)] || []).length
                      : isSource
                        ? (dashboardData?.todaysMobileAndWebGroupByAgentWiseBookings?.[selectedSource] || []).length
                        : 0}
                  </p>
                </div>
                <div style={{ background: '#F9FAFB', border: '1px solid #EAEAEA', borderRadius: 12, padding: 16 }}>
                  <h3 style={{ color: '#808080', fontSize: 14, fontWeight: '600' }}>Today's Revenue</h3>
                  <p style={{ color: '#058234', fontSize: 24, fontWeight: '700', marginTop: 8 }}>
                    ₹{isAgent
                      ? Number(dashboardData?.todaysRevenueAmountAgentWise?.[String(selectedAgent)] || 0).toFixed(2)
                      : isSource
                        ? Number(dashboardData?.todaysMobileAndWebRevenueAmountAgentWise?.[selectedSource] || 0).toFixed(2)
                        : 0}
                  </p>
                </div>
                <div style={{ background: '#F9FAFB', border: '1px solid #EAEAEA', borderRadius: 12, padding: 16 }}>
                  <h3 style={{ color: '#808080', fontSize: 14, fontWeight: '600' }}>Monthly Bookings</h3>
                  <p style={{ color: '#D57C06', fontSize: 24, fontWeight: '700', marginTop: 8 }}>
                    {isAgent
                      ? (dashboardData?.overallGroupByAgentWiseBookings?.[String(selectedAgent)] || []).length
                      : isSource
                        ? (dashboardData?.currentMonthMobileAndWebGroupByAgentWiseBookings?.[selectedSource] || []).length
                        : 0}
                  </p>
                </div>
                <div style={{ background: '#F9FAFB', border: '1px solid #EAEAEA', borderRadius: 12, padding: 16 }}>
                  <h3 style={{ color: '#808080', fontSize: 14, fontWeight: '600' }}>Monthly Revenue</h3>
                  <p style={{ color: '#032DBA', fontSize: 24, fontWeight: '700', marginTop: 8 }}>
                    ₹{isAgent
                      ? Number(dashboardData?.overallRevenueAmountAgentWise?.[String(selectedAgent)] || 0).toFixed(2)
                      : isSource
                        ? Number(dashboardData?.currentMonthMobileAndWebRevenueAmountAgentWise?.[selectedSource] || 0).toFixed(2)
                        : 0}
                  </p>
                </div>
              </div>
            </Box>
          )}
          {/* Heading and report action for the data grid */}
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography sx={{ color: '#2C2C2C', fontSize: '22px', fontFamily: 'Lato', fontWeight: '600' }}>
              {getTableTitle()}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReport}
              disabled={tableData.length === 0}
            >
              Download Report
            </Button>
          </Box>
          {/* MAIN GRID */}
          <Box style={{ width: '100%', overflowX: 'auto' }}>
            <Commongrid
              key={selectedAgent || selectedSource ? agentTab : activeTab}
              rows={rows}
              columns={getColumns()}
              totalCount={tableData.length}
              loading={loading}
              disableColumnMenu
              handleViewClick={() => { }}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
              api={() => { }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
);
};

export default KnowYourAgent;